import { useEffect, useState, useCallback, useRef } from "react";
import { CircularProgress } from "@mui/material";
import NoHistory from "./NoHistory";
import { useTranslation } from "react-i18next";
import { getMindfulnessHistory } from "api/mindfulness";
import { getMindfulnessStepsData } from "data/uiData/CardsData/MindfulnessData";
import OpenDescriptionCard from "@components/ui/OpenDescriptionCard";
import MindfulnessCards from "./MindfulnessCards";

const DashboardPastPractices = ({
  fetchHistory,
}) => {
  const { t } = useTranslation();
  const [practices, setPractices] = useState([]);
  const [status, setStatus] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Refs for tracking
  const isFetchingRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const fetchHistoryData = useCallback(
    async (page = 1, isNewFilter = false) => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      setIsLoading(true);

      try {
        const result = await getMindfulnessHistory({
          page,
          // mark_as_done: status,
        });

        if (result && result.data) {
          if (page === 1 || isNewFilter) {
            setPractices(result.data);
          } else {
            setPractices((prev) => [...prev, ...result.data]);
          }

          // Check if there are more pages
          setHasMore(result.current_page < result.last_page);
          setCurrentPage(result.current_page + 1);
        }

        setIsLoading(false);
        isFetchingRef.current = false;
        setIsInitialLoad(false);
      } catch (error) {
        console.error(
          "Error fetching Data:",
          error.response ? error.response.data : error.message
        );
        setError(error.response?.data?.message || "An error occurred");
        setIsLoading(false);
        isFetchingRef.current = false;
        setIsInitialLoad(false);
      }
    },
    [status]
  );

  // Handle scroll event with debounce
  const handleScroll = useCallback(() => {
    if (isLoading || !hasMore) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom) {
      fetchHistoryData(currentPage);
    }
  }, [currentPage, isLoading, hasMore, fetchHistoryData]);

  // Initial fetch
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setPractices([]);
    setIsInitialLoad(true);
    fetchHistoryData(1, true);
  }, [status, fetchHistoryData, fetchHistory]);

  // Add scroll event listener with debounce
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let timeoutId;
    const debouncedScrollHandler = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    scrollContainer.addEventListener("scroll", debouncedScrollHandler);
    return () => {
      scrollContainer.removeEventListener("scroll", debouncedScrollHandler);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";

      const options = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      };
      // en-GB gives dd/mm/yyyy
      return date.toLocaleDateString("en-GB", options);
    } catch (error) {
      return "N/A";
    }
  };

  return (
    <div
      className="py-4 px-7 rounded-lg bg-white  min-h-[200px]"
      // style={{ backgroundImage: `url(${BackImg})` }}
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-medium text-[black] text-lg">
          {t("mindfulnessCoachPreferences.Past Practices")}
        </h2>
      </div>

      {/* Custom scrollable container with ref */}
      <div
        ref={scrollContainerRef}
        className="space-y-3 max-h-[520px] overflow-y-auto pr-2 custom-scrollbar border border-[0.5px] border-[#E6E6E6] rounded-lg"
      >
        {isInitialLoad ? (
          <div className="text-[#0575e6] text-center py-4">
            <CircularProgress color="#0575e6" thickness={7} />
          </div>
        ) : practices?.length > 0 ? (
          practices.map((practice, index) => {
            const stepsData = getMindfulnessStepsData({
              mindfulnessStepsData: practice?.description_steps,
              t,
            });
            return (
              <div
                key={`${practice?.id}-${index}`}
                className="bg-white rounded-2xl p-4"
              >
                <div className="flex flex-col-reverse md:flex-row items-start md:items-center justify-between mb-2">
                  <h4 className="text-[18px] font-[600]">
                    {practice?.title || "Mindfulness Practice"}
                  </h4>
                  <button
                    disabled
                    className="mb-2 md:mb-0 rounded-full py-2 px-4 text-[16px] font-[600] text-white font-medium whitespace-nowrap"
                    style={{
                      background:
                        "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
                    }}
                  >
                    {t("mindfulnessCoachPreferences.Date")}:{" "}
                    {formatDate(practice?.date)}
                  </button>
                </div>
                <p className="text-gray-500 text-[18px] text-base whitespace-pre-line">
                  {practice?.description || "No description available."}
                </p>
                {practice?.description_steps && (
                  //  <OpenDescriptionCard stepsData={stepsData} />
                                 <MindfulnessCards stepsData={stepsData} />
                )}{" "}
              </div>
            );
          })
        ) : (
          <div className="text-white text-center py-4">
            <NoHistory completed={status} />
          </div>
        )}

        {isLoading && practices?.length > 0 && (
          <div className="text-[#0575e6] text-center py-4">
            <CircularProgress color="#0575e6" thickness={7} />
          </div>
        )}

        {error && <div className="text-red-300 text-center py-4">{error}</div>}

        {/* {!hasMore && practices.length > 0 && (
          <div className="text-white text-center py-4">All practices loaded</div>
        )} */}
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 12px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: #fff !important;
          border-radius: 10px;
          margin: 4px 0;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(150deg, #0771ef 12.89%, #15aafc 85.22%);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
};

export default DashboardPastPractices;
